import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Form, InputNumber, Alert, Typography, Spin } from 'antd';
import { pairTradingApi, PairCashCapacity } from '../../../services/pairTradingApi';
import { instrumentConfigApi, InstrumentConfigResponse } from '../../../services/instrumentConfigApi';
import { formatNumber, formatDecimal } from '../../../utils/numberFormat';
import { newIdempotencyKey } from '../../../utils/idempotencyKey';
import { ImportInProgress, importInProgressOf } from '../../../services/importJobApi';
import ImportInProgressModal from '../../../components/ImportJobProgress/ImportInProgressModal';

const { Text } = Typography;

/** Deriv leg = the futures contract (VN30F / VN100F, or a derivatives exchange). */
const isDerivLeg = (ic: InstrumentConfigResponse): boolean =>
  /^VN\d+F/i.test(ic.symbol) || /DS$/i.test(ic.exchange);

type Mode = 'DEPOSIT' | 'WITHDRAW';

interface Props {
  open: boolean;
  mode: Mode;
  dataConfigId: string | null;
  onCancel: () => void;
  /** Fired after a successful submit. Caller refetches its own balance/IC list. */
  onSuccess: () => void;
}

const formatVND = (s: string | number): string => {
  const n = typeof s === 'number' ? s : Number(s);
  if (!Number.isFinite(n)) return String(s);
  return formatNumber(n);
};

const STOCK_INPUT_STEP = 1000;

/** Always-visible per-symbol InstrumentConfig breakdown (grouped by leg) so the
 *  operator can monitor their configs while depositing/withdrawing. */
const PairCashResult: React.FC<{
  loading: boolean;
  items: InstrumentConfigResponse[];
}> = ({ loading, items }) => {
  const stockICs = items.filter((ic) => !isDerivLeg(ic));
  const derivICs = items.filter((ic) => isDerivLeg(ic));

  const GRID_COLS = '1.2fr 1fr 1fr 1fr 1fr';

  const renderRow = (ic: InstrumentConfigResponse) => {
    const allocated = Number(ic.currentCapital);
    const holdings = Number(ic.holdingsCost);
    const idle = allocated - holdings;
    // marketValue is cash-denominated for both legs (equity: cash + shares at market;
    // futures: cash + unrealized PnL). positionValue is the futures contract value —
    // exposure, not cash — so it's only the fallback when the leg can't be marked.
    const shown = ic.marketValue ?? ic.positionValue;
    const market =
      shown != null && Number.isFinite(Number(shown)) ? formatVND(shown) : '—';
    const upnl = ic.unrealizedPnl != null ? Number(ic.unrealizedPnl) : null;
    return (
      <div
        key={ic.id}
        style={{
          display: 'grid',
          gridTemplateColumns: GRID_COLS,
          gap: 6,
          padding: '6px 0',
          borderTop: '1px solid #2B3139',
          fontSize: 12,
        }}
      >
        <span style={{ color: '#E0E0E0', fontWeight: 500 }}>
          {ic.symbol}
        </span>
        <span style={{ textAlign: 'right', color: '#E0E0E0' }}>{formatVND(allocated)}</span>
        <span style={{ textAlign: 'right', color: '#848E9C' }}>{formatVND(holdings)}</span>
        <span style={{ textAlign: 'right', color: idle >= 0 ? '#0ECB81' : '#F6465D' }}>
          {formatVND(idle)}
        </span>
        <span style={{ textAlign: 'right', color: '#E0E0E0' }}>
          {market}
          {upnl != null && upnl !== 0 ? (
            <span
              style={{
                marginLeft: 4,
                fontSize: 10,
                color: upnl > 0 ? '#0ECB81' : '#F6465D',
              }}
            >
              ({upnl > 0 ? '+' : ''}
              {formatVND(upnl)})
            </span>
          ) : null}
        </span>
      </div>
    );
  };

  const group = (label: string, rows: InstrumentConfigResponse[]) =>
    rows.length === 0 ? null : (
      <div style={{ marginTop: 10 }}>
        <div style={{ fontSize: 11, color: '#848E9C', marginBottom: 2 }}>{label}</div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: GRID_COLS,
            gap: 6,
            fontSize: 10,
            color: '#5E6673',
            textTransform: 'uppercase',
          }}
        >
          <span>Symbol</span>
          <span style={{ textAlign: 'right' }}>Allocated</span>
          <span style={{ textAlign: 'right' }}>Holdings</span>
          <span style={{ textAlign: 'right' }}>Idle</span>
          <span style={{ textAlign: 'right' }}>Market</span>
        </div>
        {rows.map(renderRow)}
      </div>
    );

  return (
    <>
      <div style={{ marginTop: 12, fontSize: 12, color: '#848E9C' }}>
        Current instrument config of the pair:
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 20 }}><Spin /></div>
      ) : items.length === 0 ? (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 8 }}
          message="No instrument config to display yet."
        />
      ) : (
        <>
          {group('Underlying (stock)', stockICs)}
          {group('Derivatives (deriv)', derivICs)}
        </>
      )}
    </>
  );
};

/**
 * Pair-level deposit/withdraw. User enters the STOCK leg amount; the deriv leg
 * and total are auto-derived via the allocator formula `deriv = stock × m`,
 * `total = stock × (1 + m)` where `m` is the broker's floor margin rate. The
 * total is what we submit — BE re-splits with the same formula so the numbers
 * round-trip cleanly. Stock-first UX matches how operators actually think about
 * pair sizing ("I want X VND on the stock leg") rather than "I want X total".
 *
 * Blocked when the pair bot is running — UI surfaces the 409 message.
 */
const PairDepositWithdrawModal: React.FC<Props> = ({ open, mode, dataConfigId, onCancel, onSuccess }) => {
  const [form] = Form.useForm<{ stockAmount: number }>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importConflict, setImportConflict] = useState<ImportInProgress | null>(null);
  const [capacity, setCapacity] = useState<PairCashCapacity | null>(null);
  const [capacityLoading, setCapacityLoading] = useState(false);
  const [stockAmount, setStockAmount] = useState<number | null>(null);
  // The per-symbol InstrumentConfig breakdown is shown immediately when the
  // modal opens (so the operator can monitor their configs), and re-fetched
  // after a successful submit. `done` flips the primary button to "Close".
  const [ics, setIcs] = useState<InstrumentConfigResponse[] | null>(null);
  const [icsLoading, setIcsLoading] = useState(false);
  const [submitted, setSubmitted] = useState<{ stock: number; deriv: number; total: number } | null>(null);
  const [done, setDone] = useState(false);
  // One key per intent: a retry after a timeout reads back the first result instead of
  // moving the cash twice. Re-minted when the amount changes or the server answered.
  const idempotencyKeyRef = useRef<string>(newIdempotencyKey());
  const inFlightRef = useRef(false);
  useEffect(() => {
    idempotencyKeyRef.current = newIdempotencyKey();
  }, [open, mode, stockAmount]);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setError(null);
      setImportConflict(null);
      setSubmitting(false);
      setCapacity(null);
      setStockAmount(null);
      setIcs(null);
      setIcsLoading(false);
      setSubmitted(null);
      setDone(false);
    }
  }, [open, form]);

  // Load both the capacity (for the input ceiling) and the current IC list as
  // soon as the modal opens, so the breakdown is visible before any input.
  const loadIcs = React.useCallback(async () => {
    if (!dataConfigId) return;
    setIcsLoading(true);
    try {
      const list = await instrumentConfigApi.list({
        data_config_id: dataConfigId,
        config_type: 'PAIR',
        page_size: 100,
      });
      setIcs(list.items);
    } catch {
      setIcs([]); // breakdown failure shouldn't block the deposit form
    } finally {
      setIcsLoading(false);
    }
  }, [dataConfigId]);

  useEffect(() => {
    if (!open || !dataConfigId) return;
    let cancelled = false;
    setCapacityLoading(true);
    void loadIcs();
    (async () => {
      try {
        const cap = await pairTradingApi.pairCashCapacity(dataConfigId);
        if (!cancelled) setCapacity(cap);
      } catch (e: unknown) {
        const anyErr = e as { response?: { data?: { message?: string } }; message?: string };
        if (!cancelled) setError(anyErr?.response?.data?.message ?? anyErr?.message ?? 'Failed to load capacity');
      } finally {
        if (!cancelled) setCapacityLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, dataConfigId, loadIcs]);

  // Convert capacity's stock/deriv fractions into the multiplier `m = deriv/stock`.
  // `splitFraction.stock = 1/(1+m)` and `splitFraction.deriv = m/(1+m)` server-side,
  // so the ratio recovers `m` exactly without us recomputing margin × buffer here.
  const derivMultiplier = useMemo(() => {
    if (!capacity || !capacity.splitFraction.stock) return null;
    return capacity.splitFraction.deriv / capacity.splitFraction.stock;
  }, [capacity]);

  // Live preview as the user types. Floor on deriv keeps total = stock + deriv
  // exact even after rounding, matching the BE largest-remainder split.
  const preview = useMemo(() => {
    if (!capacity || derivMultiplier == null || stockAmount == null || stockAmount <= 0) return null;
    const deriv = Math.floor(stockAmount * derivMultiplier);
    const total = stockAmount + deriv;
    return { stock: stockAmount, deriv, total };
  }, [capacity, derivMultiplier, stockAmount]);

  // Stock-leg ceiling: the bigger total ceiling, projected back through the stock
  // split fraction. For DEPOSIT this is `maxDeposit × splitFraction.stock`. For
  // WITHDRAW we ALSO clamp to `stockIdle` and `derivIdle / derivMultiplier` so
  // typing a number the deriv leg can't honour gets caught client-side instead of
  // bouncing off the BE per-leg check ([[pair-cash-op-cash-bounds]]).
  const stockMax = useMemo(() => {
    if (!capacity) return undefined;
    const totalCap = Number(mode === 'DEPOSIT' ? capacity.maxDeposit : capacity.maxWithdraw);
    if (!Number.isFinite(totalCap) || totalCap <= 0) return undefined;
    const fromTotal = totalCap * capacity.splitFraction.stock;
    if (mode === 'DEPOSIT') return Math.floor(fromTotal);
    const stockIdle = Number(capacity.stockIdle);
    const derivIdle = Number(capacity.derivIdle);
    const fromDerivIdle = derivMultiplier && derivMultiplier > 0
      ? derivIdle / derivMultiplier
      : Number.POSITIVE_INFINITY;
    return Math.floor(Math.min(fromTotal, stockIdle, fromDerivIdle));
  }, [capacity, derivMultiplier, mode]);

  const willExceed = stockAmount != null && stockMax != null && stockAmount > stockMax;
  // Gate on the PAIR-only flag, not the broad botActive: a GRID running on the
  // same equity account must NOT block pair deposit/withdraw (BE.depositToPair
  // already scopes its guard to isPairExecutionActive). Only a live PAIR worker,
  // which holds allocation state in memory, requires stop-first.
  const pairBotActive = capacity?.pairBotActive === true;
  const submitDisabled = pairBotActive || willExceed || stockAmount == null || stockAmount <= 0;

  const handleSubmit = async () => {
    if (!dataConfigId) return;
    // A click landing before `submitting` re-renders the button must not send a second op.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      await form.validateFields();
      if (!preview) return;
      setSubmitting(true);
      setError(null);
      // Submit the derived TOTAL — BE.depositToPair / withdrawFromPair both use
      // the same allocator math to split back, so stockCash and derivCash on the
      // server match what we just previewed (modulo the largest-remainder
      // per-IC fanout for multi-stock baskets, which is invisible at this level).
      const key = idempotencyKeyRef.current;
      if (mode === 'DEPOSIT') {
        await pairTradingApi.depositToPair(dataConfigId, preview.total, key);
      } else {
        await pairTradingApi.withdrawFromPair(dataConfigId, preview.total, key);
      }
      // Mark done + re-fetch the IC breakdown so the always-visible panel
      // reflects the new allocations. The modal stays open for monitoring;
      // closing it refreshes the parent.
      setSubmitted({ stock: preview.stock, deriv: preview.deriv, total: preview.total });
      setDone(true);
      await loadIcs();
    } catch (e: unknown) {
      if (e && typeof e === 'object' && 'errorFields' in e) return;
      const anyErr = e as { response?: { status?: number; data?: { message?: string } }; message?: string };
      // The server answered: this intent is settled, a retry is a new request. No answer
      // (timeout / network) keeps the key so the retry reads the outcome.
      if (anyErr?.response?.status != null) idempotencyKeyRef.current = newIdempotencyKey();
      // An open import on this pair's namespace: show its progress, not a dead-end error.
      const open409 = importInProgressOf(e);
      if (open409) {
        setImportConflict(open409);
        return;
      }
      const msg = anyErr?.response?.data?.message ?? anyErr?.message ?? String(e);
      setError(msg);
    } finally {
      inFlightRef.current = false;
      setSubmitting(false);
    }
  };

  // After a committed deposit/withdraw, both "Close" and the X/mask should close
  // AND refresh the parent; before that, the X just cancels.
  const handleModalCancel = () => {
    if (done) onSuccess();
    else onCancel();
  };

  const title = mode === 'DEPOSIT' ? 'Deposit to pair' : 'Withdraw from pair';

  return (
    <Modal
      title={title}
      open={open}
      onCancel={handleModalCancel}
      onOk={done ? handleModalCancel : handleSubmit}
      okText={done ? 'Close' : (mode === 'DEPOSIT' ? 'Deposit' : 'Withdraw')}
      centered
      styles={{ body: { padding: '20px 24px' } }}
      okButtonProps={done ? {
        style: { background: '#0ECB81', borderColor: '#0ECB81' },
      } : {
        loading: submitting,
        disabled: submitDisabled,
        style: mode === 'DEPOSIT'
          ? { background: '#0ECB81', borderColor: '#0ECB81' }
          : { background: '#F6465D', borderColor: '#F6465D' },
      }}
      cancelButtonProps={{ style: done ? { display: 'none' } : undefined }}
      destroyOnClose
    >
      {capacityLoading && !capacity ? (
        <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
      ) : (
        <>
          {capacity && (
            <div style={{
              marginBottom: 12,
              padding: '8px 12px',
              background: '#1E2329',
              border: '1px solid #2B3139',
              borderRadius: 6,
              fontSize: 12,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <Text type="secondary">Max stock-leg {mode === 'DEPOSIT' ? 'deposit' : 'withdraw'}</Text>
                <Text strong style={{ color: mode === 'DEPOSIT' ? '#0ECB81' : '#F6465D' }}>
                  {stockMax != null ? `${formatVND(stockMax)} VND` : '—'}
                </Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#848E9C' }}>
                <span>Allocator split</span>
                <span>
                  {formatDecimal(capacity.splitFraction.stock * 100, 1)}% stock /{' '}
                  {formatDecimal(capacity.splitFraction.deriv * 100, 1)}% deriv
                </span>
              </div>
              {mode === 'WITHDRAW' && (
                <div style={{ display: 'flex', justifyContent: 'space-between', color: '#848E9C', marginTop: 2 }}>
                  <span>Idle: stock {formatVND(capacity.stockIdle)}</span>
                  <span>deriv {formatVND(capacity.derivIdle)}</span>
                </div>
              )}
            </div>
          )}

          <Form form={form} layout="vertical">
            <Form.Item
              name="stockAmount"
              label={`Stock leg ${mode === 'DEPOSIT' ? 'deposit' : 'withdraw'} (VND)`}
              rules={[
                { required: true, message: 'Stock leg amount is required' },
                { type: 'number', min: 1, message: 'Amount must be > 0' },
              ]}
              extra="Deriv leg + total are auto-derived from the allocator formula."
            >
              <InputNumber<number>
                style={{ width: '100%' }}
                min={1}
                max={stockMax}
                step={STOCK_INPUT_STEP}
                disabled={pairBotActive}
                formatter={(v) => (v === undefined || v === null ? '' : formatNumber(Number(v)))}
                parser={((v?: string) => (v ? Number(v.replace(/[^\d]/g, '')) : 0)) as never}
                onChange={(v) => setStockAmount(typeof v === 'number' ? v : null)}
              />
            </Form.Item>
          </Form>

          {/* Stock → Deriv → Total preview. Mirrors the BE allocator so what the
              user sees is what gets persisted (subject to per-IC rounding). */}
          {preview && (
            <div style={{
              marginTop: 4,
              padding: '10px 12px',
              background: '#1E2329',
              border: '1px solid #2B3139',
              borderRadius: 6,
              display: 'grid',
              // Three hard tracks are ~105px on a phone, and "1,234,567,890 VND" does not
              // fit one — a grid track's default min-width:auto then widened the column and
              // the box overflowed the modal. auto-fit drops it to two, then one.
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(120px, 100%), 1fr))',
              gap: 8,
            }}>
              <div>
                <div style={{ fontSize: 11, color: '#848E9C', marginBottom: 2 }}>Stock leg</div>
                <div style={{ fontSize: 13, color: '#E0E0E0', fontWeight: 500 }}>
                  {formatVND(preview.stock)} VND
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#848E9C', marginBottom: 2 }}>Deriv leg (auto)</div>
                <div style={{ fontSize: 13, color: '#E0E0E0', fontWeight: 500 }}>
                  {formatVND(preview.deriv)} VND
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, color: '#848E9C', marginBottom: 2 }}>Total</div>
                <div style={{
                  fontSize: 13,
                  color: mode === 'DEPOSIT' ? '#0ECB81' : '#F6465D',
                  fontWeight: 600,
                }}>
                  {formatVND(preview.total)} VND
                </div>
              </div>
            </div>
          )}
          {willExceed && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
              message={
                mode === 'DEPOSIT'
                  ? `Stock leg exceeds capacity. Max is ${stockMax != null ? formatVND(stockMax) : '—'} VND — broker PP would be breached on one of the legs.`
                  : `Stock leg exceeds idle. Max is ${stockMax != null ? formatVND(stockMax) : '—'} VND — close positions or use per-symbol withdraw to free more.`
              }
            />
          )}
          {pairBotActive && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
              message="The pair bot is running. Stop the pair before depositing or withdrawing."
            />
          )}
          {/* Allocator warnings panel (e.g. deriv marginStatus CLOSE_ONLY) is
              hidden by request — purely informational, does not gate the
              deposit/withdraw flow or the bot. capacity.warnings still drives
              nothing else, so the data fetch is left intact. */}
          {error && <Alert type="error" showIcon style={{ marginTop: 8 }} message={error} />}

          {done && submitted && (
            <Alert
              type="success"
              showIcon
              style={{ marginTop: 12 }}
              message={`${mode === 'DEPOSIT' ? 'Deposited' : 'Withdrew'} ${formatVND(submitted.total)} VND (underlying ${formatVND(submitted.stock)} · derivatives ${formatVND(submitted.deriv)}).`}
            />
          )}
        </>
      )}

      {/* Always-visible per-symbol IC breakdown — rendered outside the capacity
          gate so the operator sees their instrument configs immediately, even
          while the deposit capacity is still loading. */}
      <PairCashResult loading={icsLoading} items={ics ?? []} />
      <ImportInProgressModal conflict={importConflict} onClose={() => setImportConflict(null)} />
    </Modal>
  );
};

export default PairDepositWithdrawModal;
